/**
 * Email Domain Service
 *
 * Handles API calls for managing email domains in the superadmin dashboard.
 */

import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../utils/api';

/**
 * List all email domains
 * @param {boolean} includeInactive - Whether to include inactive domains
 * @returns {Promise<Array>} List of domain records
 */
export async function listEmailDomains(includeInactive = false) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();
  const params = includeInactive ? '?include_inactive=true' : '';
  const response = await axios.get(`${apiBase}/admin/email-domains${params}`, { headers });
  return response.data;
}

/**
 * Add a new email domain
 * @param {Object} domainData - Domain data
 * @param {string} domainData.domain - Domain string
 * @param {string} domainData.label - Human-readable label
 * @param {string} [domainData.description] - Description
 * @param {boolean} [domainData.is_active] - Whether domain is active
 * @returns {Promise<Object>} Created domain record
 */
export async function addEmailDomain(domainData) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();
  const response = await axios.post(`${apiBase}/admin/email-domains`, domainData, { headers });
  return response.data;
}

/**
 * Update an email domain
 * @param {string} domainId - Domain ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated domain record
 */
export async function updateEmailDomain(domainId, updates) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();
  const response = await axios.put(`${apiBase}/admin/email-domains/${domainId}`, updates, { headers });
  return response.data;
}

/**
 * Set a domain as primary
 * @param {string} domainId - Domain ID to make primary
 * @returns {Promise<Object>} Updated domain record
 */
export async function setPrimaryDomain(domainId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();
  const response = await axios.put(`${apiBase}/admin/email-domains/${domainId}/set-primary`, {}, { headers });
  return response.data;
}

/**
 * Delete an email domain
 * @param {string} domainId - Domain ID to delete
 * @returns {Promise<Object>} Success response
 */
export async function deleteEmailDomain(domainId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();
  const response = await axios.delete(`${apiBase}/admin/email-domains/${domainId}`, { headers });
  return response.data;
}

/**
 * Get the primary email domain
 * @returns {Promise<string>} Primary domain string
 */
export async function getPrimaryEmailDomain() {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();
  const response = await axios.get(`${apiBase}/admin/email-domains/primary`, { headers });
  return response.data.domain;
}
